/**
 * FireGuard Live - API Service
 * Axios конфигурация за комуникация с Node.js backend
 */
import axios from 'axios';

// Base URL for the API - with explicit debugging
// В production НИКОГА не използваме localhost – иначе на телефон се зареждат грешни/мок данни.
// Винаги: текущ origin (от URL в браузъра) или https://app.firesguard.net
const envUrl = process.env.REACT_APP_API_URL;
const isProduction = process.env.NODE_ENV === 'production';
const runtimeOrigin = typeof window !== 'undefined' && window.location && window.location.origin ? window.location.origin : '';

function getProductionBaseUrl() {
  const origin = runtimeOrigin && !runtimeOrigin.startsWith('file:') ? runtimeOrigin : 'https://app.firesguard.net';
  return origin.replace(/\/+$/, '');
}

let baseUrl;
if (isProduction) {
  baseUrl = getProductionBaseUrl();
} else {
  baseUrl = (envUrl || 'http://localhost:5000').replace(/\/+$/, '');
}
// Добави /api ако липсва
if (!baseUrl.endsWith('/api')) {
  baseUrl = `${baseUrl}/api`;
}
const API_BASE_URL = baseUrl;

const productionOrigin = getProductionBaseUrl();
const API_ORIGIN = isProduction ? productionOrigin : (process.env.REACT_APP_WS_URL || (envUrl ? envUrl.replace(/\/api\/?$/i, '') : null) || 'http://localhost:5000');
const AI_SERVICE_BASE_URL = isProduction ? (productionOrigin + '/ai') : (process.env.REACT_APP_AI_SERVICE_URL || process.env.REACT_APP_AI_WS_URL || 'http://localhost:3001');

// Експортирай за използване в други компоненти
export { API_BASE_URL, API_ORIGIN, AI_SERVICE_BASE_URL };

// Създай axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000, // 15 секунди timeout (увеличено за бавни заявки)
  headers: {
    'Content-Type': 'application/json',
  },
  // Изключи кеширането за dashboard заявки
  validateStatus: function (status) {
    return status >= 200 && status < 500; // Приемаме 304 като валиден статус
  },
  // Добави retry логика за network грешки
  retry: 0, // Не retry автоматично, но можем да добавим manual retry
});

// Request interceptor - добави JWT token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('fireguard_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Изключи кеширането за dashboard заявки
    if (config.url.includes('/monitoring/dashboard')) {
      config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
      config.headers['Pragma'] = 'no-cache';
      config.headers['Expires'] = '0';
      // Добави timestamp параметър за да форсираме нов response (избягваме 304)
      const separator = config.url.includes('?') ? '&' : '?';
      config.url = `${config.url}${separator}_t=${Date.now()}`;
    }
    
    const fullUrl = `${config.baseURL}${config.url}`;
    if (process.env.NODE_ENV === 'development' && !config.url?.includes('/auth/login')) {
      console.log('%c[API] Request:', 'color: blue;', config.method?.toUpperCase(), config.url);
    }
    if (config.url?.includes('/monitoring/dashboard') && !fullUrl.includes('/api/monitoring/dashboard')) {
      console.error('%c[API] Dashboard wrong URL', 'color: red;', fullUrl);
    }
    return config;
  },
  (error) => {
    console.error('[API] Request interceptor error:', error);
    return Promise.reject(error);
  }
);

/** Проверка дали отговорът е HTML (index.html) вместо JSON – сървърът връща SPA вместо API */
function isHtmlResponse(data) {
  if (data == null || typeof data !== 'string') return false;
  const t = data.trim().toLowerCase();
  return t.startsWith('<!') || t.startsWith('<html') || t.includes('<!doctype');
}

// Response interceptor - обработка на грешки
api.interceptors.response.use(
  (response) => {
    // 401 винаги да влиза в error path, за да се покаже съобщението в формата за вход (не само в конзолата)
    if (response.status === 401) {
      const err = new Error(response.data?.message || 'Необходима е автентикация.');
      err.response = response;
      err.config = response.config;
      return Promise.reject(err);
    }
    if (isHtmlResponse(response.data)) {
      const base = process.env.REACT_APP_API_URL || '';
      console.error('%c[API] Сървърът върна HTML вместо JSON.', 'color: red; font-weight: bold;');
      console.error('Заявката отива към:', response.config?.baseURL || base, response.config?.url);
      const msg = base && base.includes('app.firesguard.net')
        ? 'API is returning HTML instead of data. Cloudflare Tunnel must route /api to the backend (port 5000). Check /etc/cloudflared/config.yml and restart: sudo systemctl restart cloudflared.'
        : 'API is returning HTML instead of data. Check that the backend is running and REACT_APP_API_URL points to the correct URL (e.g. https://app.firesguard.net with Cloudflare).';
      return Promise.reject(new Error(msg));
    }
    if (process.env.NODE_ENV === 'development' && !response.config?.url?.includes('/auth/login')) {
      console.log('%c[API] OK', 'color: green;', response.config?.url, response.status);
    }
    // Нормализация на dashboard отговор – винаги връщаме обект с .dashboard, за да се зареждат секциите дори при липса на устройства
    if (response.config.url.includes('/monitoring/dashboard') && response.status === 200) {
      const emptyDashboard = {
        deviceStats: {},
        criticalAlerts: 0,
        overdueMaintenance: 0,
        recentInspections: { total: 0, passed: 0, failed: 0 },
        devicesNeedingAttention: [],
        recentActivity: []
      };
      const data = response.data;
      const isObject = data && typeof data === 'object' && !Array.isArray(data);
      const hasValidDashboard = isObject && data.dashboard && typeof data.dashboard === 'object' && !Array.isArray(data.dashboard);
      const hasFlatDashboard = isObject && (data.deviceStats !== undefined || data.criticalAlerts !== undefined || data.recentInspections !== undefined);

      if (!hasValidDashboard && !hasFlatDashboard) {
        console.warn('%c[API] Dashboard: неочакван формат (очакван е обект), използваме празни данни', 'color: orange;', data === undefined ? 'undefined' : data === null ? 'null' : typeof data);
        response.data = { success: true, dashboard: emptyDashboard };
      } else if (hasValidDashboard) {
        // Оставяме както е
      } else if (hasFlatDashboard) {
        response.data = { success: true, dashboard: { ...emptyDashboard, ...data } };
      }
    }

    if (response.config.url.includes('/monitoring/dashboard')) {
      console.log('%c[API] Dashboard response data:', 'color: green; font-weight: bold;', response.data);
    }

    return response;
  },
  (error) => {
    if (process.env.NODE_ENV === 'development' && !error.config?.url?.includes('/auth/login')) {
      console.error('%c[API] Error', 'color: red;', error.config?.url, error.response?.status, error.response?.data?.message || error.message);
    }
    // Специална обработка за dashboard заявки
    if (error.config?.url?.includes('/monitoring/dashboard')) {
      console.error('%c❌ Dashboard заявката се провали:', 'color: red; font-weight: bold; font-size: 16px;');
      
      if (!error.response) {
        console.error('%c❌ Няма response от backend!', 'color: red; font-weight: bold;');
        console.error('Възможни причини:');
        console.error('1. Backend сървърът не работи');
        console.error('2. CORS проблем');
        console.error('3. Network грешка');
        console.error('Error details:', error);
      } else if (error.response.status === 401) {
        console.error('%c❌ 401 Unauthorized - невалиден token!', 'color: red; font-weight: bold;');
      } else if (error.response.status === 500) {
        console.error('%c❌ 500 Server Error - backend грешка!', 'color: red; font-weight: bold;');
        console.error('Response data:', error.response.data);
      } else if (!error.response.data || Object.keys(error.response.data || {}).length === 0) {
        console.error('%c❌ Празен response data от backend!', 'color: red; font-weight: bold;');
        console.error('Response:', error.response);
      }
    }
    
    if (error.response?.status === 401) {
      const isLoginRequest = (error.config?.url || '').includes('/auth/login');
      if (!isLoginRequest) {
        console.warn('%c[API] 401 Unauthorized - redirecting to login', 'color: orange; font-weight: bold;');
        localStorage.removeItem('fireguard_token');
        localStorage.removeItem('fireguard_user');
        if (typeof window !== 'undefined' && window.location.hash !== '#/') {
          window.location.hash = '#/';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (username, password) => api.post('/auth/login', { username, password }),
  register: (data) => api.post('/auth/register-public', data),
  logout: () => api.post('/auth/logout'),
  refreshToken: () => api.post('/auth/refresh'),
  getCurrentUser: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.post('/auth/change-password', data),
  getSessions: () => api.get('/auth/sessions'),
  terminateSession: (sessionId) => api.delete(`/auth/sessions/${sessionId}`),
};

// Devices API
export const devicesAPI = {
  getAll: (params) => api.get('/devices', { params }),
  getOne: (id) => api.get(`/devices/${id}`),
  create: (data) => api.post('/devices', data),
  update: (id, data) => api.put(`/devices/${id}`, data),
  delete: (id) => api.delete(`/devices/${id}`),
  scanQR: (qrData) => api.post('/devices/scan-qr', { qrData }),
  regenerateQR: (id) => api.post(`/devices/${id}/qr-regenerate`),
  getHistory: (id, params) => api.get(`/devices/${id}/history`, { params }),
  getStatistics: () => api.get('/devices/stats/overview'),
};

// Monitoring API
export const monitoringAPI = {
  getDashboard: async () => {
    console.log('%c[monitoringAPI] getDashboard called', 'color: blue; font-weight: bold;');
    try {
      const response = await api.get('/monitoring/dashboard');
      console.log('%c[monitoringAPI] getDashboard response received', 'color: green; font-weight: bold;', {
        status: response.status,
        statusText: response.statusText,
        hasData: !!response.data,
        dataType: typeof response.data,
        dataKeys: response.data ? Object.keys(response.data) : [],
        headers: response.headers
      });
      
      const emptyDashboard = {
        deviceStats: {},
        criticalAlerts: 0,
        overdueMaintenance: 0,
        recentInspections: { total: 0, passed: 0, failed: 0 },
        devicesNeedingAttention: [],
        recentActivity: []
      };
      // При липса на данни връщаме празен dashboard обект, за да се зареждат секциите (устройства, поддръжка, инспекции)
      if (!response || response.data === undefined || response.data === null) {
        console.warn('%c[monitoringAPI] Празен response – връщаме празен dashboard', 'color: orange;');
        return { data: { success: true, dashboard: emptyDashboard } };
      }
      if (typeof response.data !== 'object' || Array.isArray(response.data)) {
        console.warn('%c[monitoringAPI] Response.data не е обект – нормализираме', 'color: orange;');
        return { data: { success: true, dashboard: emptyDashboard } };
      }
      
      return response;
    } catch (error) {
      console.warn('%c[monitoringAPI] getDashboard грешка – връщаме празен dashboard, за да се покажат секциите', 'color: orange;', error?.message || error);
      const emptyDashboard = {
        deviceStats: {},
        criticalAlerts: 0,
        overdueMaintenance: 0,
        recentInspections: { total: 0, passed: 0, failed: 0 },
        devicesNeedingAttention: [],
        recentActivity: []
      };
      return { data: { success: true, dashboard: emptyDashboard } };
    }
  },
  getAlerts: (params) => api.get('/monitoring/alerts', { params }),
  resolveAlert: (id, data) => api.post(`/monitoring/alerts/${id}/resolve`, data),
  createAlert: (data) => api.post('/monitoring/alerts', data),
  getDeviceStatus: (id) => api.get(`/monitoring/device-status/${id}`),
  getStatistics: () => api.get('/monitoring/statistics'),
  getHealthCheck: () => api.get('/monitoring/health-check'),
};

// Maintenance API
export const maintenanceAPI = {
  getAll: (params) => api.get('/maintenance', { params }),
  getOne: (id) => api.get(`/maintenance/${id}`),
  create: (data) => api.post('/maintenance', data),
  update: (id, data) => api.put(`/maintenance/${id}`, data),
  delete: (id) => api.delete(`/maintenance/${id}`),
  getMyTasks: (params) => api.get('/maintenance/my-tasks/list', { params }),
  getOverdue: (params) => api.get('/maintenance/overdue/list', { params }),
  assign: (id, assigned_to) => api.post(`/maintenance/${id}/assign`, { assigned_to }),
  getStatistics: () => api.get('/maintenance/stats/overview'),
};

// Monitor APIs
export const sprinklerMonitorAPI = {
  getZones: () => api.get('/sprinkler-monitor/zones'),
  getZone: (id) => api.get(`/sprinkler-monitor/zones/${id}`),
  createZone: (data) => api.post('/sprinkler-monitor/zones', data),
  updateZone: (id, data) => api.put(`/sprinkler-monitor/zones/${id}`, data),
  sendSignals: (id, signals) => api.post(`/sprinkler-monitor/zones/${id}/signals`, signals),
  getZoneEvents: (id, limit) => api.get(`/sprinkler-monitor/zones/${id}/events`, { params: { limit } }),
};

export const electricalPanelMonitorAPI = {
  getPanels: () => api.get('/electrical-panel-monitor/panels'),
  getPanel: (id) => api.get(`/electrical-panel-monitor/panels/${id}`),
  createPanel: (data) => api.post('/electrical-panel-monitor/panels', data),
  updatePanel: (id, data) => api.put(`/electrical-panel-monitor/panels/${id}`, data),
  sendSignals: (id, signals) => api.post(`/electrical-panel-monitor/panels/${id}/signals`, signals),
  getPanelEvents: (id, limit) => api.get(`/electrical-panel-monitor/panels/${id}/events`, { params: { limit } }),
};

export const evacDoorMonitorAPI = {
  getDoors: () => api.get('/evac-door-monitor/doors'),
  getDoor: (id) => api.get(`/evac-door-monitor/doors/${id}`),
  createDoor: (data) => api.post('/evac-door-monitor/doors', data),
  updateDoor: (id, data) => api.put(`/evac-door-monitor/doors/${id}`, data),
  sendSignals: (id, signals) => api.post(`/evac-door-monitor/doors/${id}/signals`, signals),
  getDoorEvents: (id, limit) => api.get(`/evac-door-monitor/doors/${id}/events`, { params: { limit } }),
};

// Inspections API
export const inspectionsAPI = {
  getAll: (params) => api.get('/inspections', { params }),
  getOne: (id) => api.get(`/inspections/${id}`),
  create: (data) => api.post('/inspections', data),
  update: (id, data) => api.put(`/inspections/${id}`, data),
  getByDevice: (deviceId, params) => api.get(`/inspections/device/${deviceId}`, { params }),
  getMyInspections: (params) => api.get('/inspections/my-inspections/list', { params }),
  getOverdue: (params) => api.get('/inspections/overdue/list', { params }),
  scheduleFollowup: (id, data) => api.post(`/inspections/${id}/schedule-followup`, data),
  getStatistics: () => api.get('/inspections/stats/overview'),
};

export default api;
